import { useCallback, useEffect, useState } from "react";

import { CheckCircle2, Clock, XCircle } from "lucide-react";

import {
  HubPageLoading,
  hubModalLoadingClassName,
} from "../../Components/EngagementEmptyPanel";
import { TierPill } from "../../Pages/MyCheckIns/EngagementBadges";
import type {
  Poll1Question,
  Poll1Tier,
} from "../../Pages/MyCheckIns/poll1Helpers";
import {
  poll1Lookup,
  poll1RawFromAnswers,
} from "../../Pages/MyCheckIns/poll1Helpers";
import { useAppDispatch } from "../../Store/Hooks";
import { fetchCycleParticipantResponses } from "../../Store/Reducers/SponsorEngagementSlice";
import { MCI_MUTED } from "../../Utils/Helper/sponsorEngagementUi";

// ── Types ──────────────────────────────────────────────────────────────────

export type PollResponseTarget =
  | {
      kind: "poll1";
      sponsorId: string;
      cycleId: string;
      period?: string;
      sponsorName?: string;
    }
  | {
      kind: "poll2";
      teamMemberId: string;
      cycleId: string;
      period?: string;
      teamMemberName?: string;
    };

type Poll1AccountRow = {
  account_id: string;
  account_name: string;
  poll1_submitted_at?: string | null;
  poll1_responses?: (boolean | null)[] | null;
  poll1_evidence?: Record<string, string> | null;
  poll1_notes?: string | null;
  poll1_raw?: number | null;
};

type Poll2AccountRow = {
  account_id: string;
  account_name: string;
  sponsor_name?: string;
  poll2_submitted_at?: string | null;
  poll2_dimensions?: Record<string, number> | null;
  poll2_raw?: number | null;
  poll2_evidence?: Record<string, string> | null;
};

type Poll1Data = {
  sponsor_name: string;
  period: string;
  questions: Poll1Question[];
  poll1_tiers: Poll1Tier[];
  accounts: Poll1AccountRow[];
};

type Poll2Data = {
  team_member_name: string;
  period: string;
  accounts: Poll2AccountRow[];
};

/** Builds this target's modal title/subtitle — no fetch needed, just the target's own fields. */
export const getPollResponseTitle = (
  target: PollResponseTarget
): { title: string; subtitle?: string } => ({
  title:
    target.kind === "poll1"
      ? `Poll 1 Responses · ${target.sponsorName || "Sponsor"}`
      : `Poll 2 Feedback · ${target.teamMemberName || "Team member"}`,
  subtitle: target.period,
});

// ── Poll1 account detail ───────────────────────────────────────────────────

const Poll1AccountDetail = ({
  row,
  questions,
  tiers,
}: {
  row: Poll1AccountRow;
  questions: Poll1Question[];
  tiers: Poll1Tier[];
}) => {
  const submitted = Boolean(row.poll1_submitted_at);
  const answers: (boolean | null)[] = (row.poll1_responses ?? []).map(v =>
    typeof v === "boolean" ? v : null
  );
  const score = submitted ? poll1RawFromAnswers(answers, questions) : 0;
  const tierObj = submitted ? poll1Lookup(score, tiers) : null;

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      {/* Account header */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100">
        <div>
          <div className="text-sm font-semibold text-gray-900">
            {row.account_name}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {submitted ? (
            <>
              {tierObj && <TierPill tier={tierObj.tier} />}
              <span className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                <CheckCircle2 size={12} />
                Submitted
              </span>
            </>
          ) : (
            <span className="flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5">
              <Clock size={12} />
              Not submitted
            </span>
          )}
        </div>
      </div>

      {submitted && questions.length > 0 ? (
        <div className="px-4 py-3 space-y-2.5">
          {questions.map((q, i) => {
            const ans = answers[i];
            const yes = ans === true;
            const no = ans === false;
            const evidence = row.poll1_evidence?.[q.id] ?? "";
            const showEvidence =
              yes && q.evidence_type && q.evidence_type !== "none" && evidence;
            return (
              <div
                key={q.id}
                className={`rounded-lg border px-3.5 py-3 ${
                  yes
                    ? "border-green-200 bg-green-50/40"
                    : no
                      ? "border-red-200 bg-red-50/40"
                      : "border-gray-200 bg-white"
                }`}
              >
                <div className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-bold text-gray-700 mt-0.5">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold leading-snug text-gray-900">
                      {q.text}
                    </div>
                    <div className={`text-[11px] ${MCI_MUTED} mt-0.5`}>
                      {q.window}
                    </div>
                  </div>
                  <div className="shrink-0 flex items-center gap-1.5">
                    {yes ? (
                      <span className="flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 rounded-md px-2 py-0.5">
                        <CheckCircle2 size={12} />
                        Yes
                      </span>
                    ) : no ? (
                      <span className="flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-100 rounded-md px-2 py-0.5">
                        <XCircle size={12} />
                        No
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                    <span className="text-[10px] font-bold text-purple-800 bg-purple-100 px-1.5 py-0.5 rounded-md">
                      +{q.pts}pt{q.pts !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
                {showEvidence && (
                  <div className="mt-2 ml-9 text-[12px] text-gray-700 bg-white border border-gray-200 rounded px-2.5 py-1.5">
                    <span className="text-[10.5px] font-semibold text-gray-500 uppercase tracking-wide mr-1.5">
                      {q.evidence_label ?? "Evidence"}:
                    </span>
                    {evidence}
                  </div>
                )}
              </div>
            );
          })}
          {row.poll1_notes && (
            <div className="mt-1 text-[12.5px] text-gray-700 bg-gray-50 border border-gray-200 rounded px-3 py-2">
              <span className="text-[10.5px] font-semibold text-gray-500 uppercase tracking-wide mr-1.5">
                Notes:
              </span>
              {row.poll1_notes}
            </div>
          )}
        </div>
      ) : (
        !submitted && (
          <div className="px-4 py-4 text-sm text-gray-400 text-center">
            No responses submitted yet for this account.
          </div>
        )
      )}
    </div>
  );
};

// ── Poll2 dimension display ────────────────────────────────────────────────

const DIMENSION_LABELS: Record<string, string> = {
  involvement: "Involvement",
  relationship: "Relationship",
  knowledge: "Product Knowledge",
};

const Poll2AccountDetail = ({ row }: { row: Poll2AccountRow }) => {
  const submitted = Boolean(row.poll2_submitted_at);
  const dims = row.poll2_dimensions ?? {};
  const dimEntries = Object.entries(DIMENSION_LABELS);

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100">
        <div>
          <div className="text-sm font-semibold text-gray-900">
            {row.account_name}
          </div>
          {row.sponsor_name && (
            <div className={`text-xs ${MCI_MUTED} mt-0.5`}>
              Sponsor: {row.sponsor_name}
            </div>
          )}
        </div>
        <div className="shrink-0">
          {submitted ? (
            <span className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
              <CheckCircle2 size={12} />
              Submitted
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5">
              <Clock size={12} />
              Not submitted
            </span>
          )}
        </div>
      </div>
      {submitted && dimEntries.length > 0 ? (
        <div className="px-4 py-3 grid grid-cols-3 gap-3">
          {dimEntries.map(([key, label]) => {
            const val = dims[key];
            return (
              <div
                key={key}
                className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-center"
              >
                <div
                  className={`text-[10.5px] font-semibold uppercase tracking-wide ${MCI_MUTED} mb-1`}
                >
                  {label}
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {val != null ? val : "—"}
                </div>
                <div className={`text-[10px] ${MCI_MUTED}`}>/ 5</div>
              </div>
            );
          })}
        </div>
      ) : (
        !submitted && (
          <div className="px-4 py-4 text-sm text-gray-400 text-center">
            No feedback submitted for this account.
          </div>
        )
      )}
    </div>
  );
};

// ── Content (no modal wrapper — the parent renders it inside its own dialog) ─

type Props = {
  target: PollResponseTarget;
};

const SponsorPollResponseContent = ({ target }: Props) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [poll1Data, setPoll1Data] = useState<Poll1Data | null>(null);
  const [poll2Data, setPoll2Data] = useState<Poll2Data | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPoll1Data(null);
    setPoll2Data(null);
    try {
      const result = await dispatch(
        fetchCycleParticipantResponses({
          cycleId: target.cycleId,
          sponsorId: target.kind === "poll1" ? target.sponsorId : undefined,
          teamMemberId:
            target.kind === "poll2" ? target.teamMemberId : undefined,
        })
      );
      if (fetchCycleParticipantResponses.fulfilled.match(result)) {
        const payload = result.payload as Poll1Data | Poll2Data;
        if (target.kind === "poll1") {
          setPoll1Data(payload as Poll1Data);
        } else {
          setPoll2Data(payload as Poll2Data);
        }
      } else {
        throw new Error((result.payload as string) || "Failed to load");
      }
    } catch (e: unknown) {
      setError((e as any)?.message || "Failed to load responses");
    } finally {
      setLoading(false);
    }
  }, [dispatch, target]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      {loading ? (
        <HubPageLoading
          message="Loading responses…"
          className={hubModalLoadingClassName}
        />
      ) : null}
      {!loading && error ? (
        <div className="flex h-full min-h-0 flex-1 items-center justify-center text-red-600 text-sm">
          {error}
        </div>
      ) : null}
      {!loading && !error && poll1Data ? (
        <div className="space-y-4">
          <div className={`text-sm ${MCI_MUTED}`}>
            {poll1Data.accounts.filter(a => a.poll1_submitted_at).length} of{" "}
            {poll1Data.accounts.length} accounts submitted
          </div>
          {poll1Data.accounts.map(a => (
            <Poll1AccountDetail
              key={a.account_id}
              row={a}
              questions={poll1Data.questions}
              tiers={poll1Data.poll1_tiers}
            />
          ))}
        </div>
      ) : null}
      {!loading && !error && poll2Data ? (
        <div className="space-y-4">
          <div className={`text-sm ${MCI_MUTED}`}>
            {poll2Data.accounts.filter(a => a.poll2_submitted_at).length} of{" "}
            {poll2Data.accounts.length} accounts submitted
          </div>
          {poll2Data.accounts.map(a => (
            <Poll2AccountDetail key={a.account_id} row={a} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default SponsorPollResponseContent;
